import { format } from 'date-fns';
import { useQuery } from 'react-query';
import useFetchUserDetails from './useFetchUserDetails';
import useFetchCompensationDetails from './useFetchCompensationDetails';
import { AppError } from 'utils/AppError';
import { FetchCompensationDetailsResponse } from './useFetchCompensationDetails';
import api from 'api';

export type PayDetailsData = {
  userData: any;
  compensationData?: FetchCompensationDetailsResponse;
  hasPendingRevisionRequest?: boolean;
  firstName: string | null;
  error: AppError | null;
  isLoading: boolean;
  refetch: () => void;
};

const useFetchPayDetailsData = (userId: string | null): PayDetailsData => {
  const {
    data: userData,
    firstName,
    error: userError,
    isLoading: isUserDataLoading,
    refetch: refetchUserData,
  } = useFetchUserDetails(userId);
  
  // Fetch salary details to get ecssProfileDisplayMonth — used for mid-month ECSS revisions
  // so the CTC component breakdown shows old/new values, never prorated values.
  const {
    data: salaryDetailsData,
    isLoading: isSalaryDetailsLoading,
    refetch: refetchSalaryDetails,
  } = useQuery(
    ['payDetailsSalaryDetails', userId],
    () => api.userQueries.getSalaryDetails({ userId: userId! }),
    { enabled: !!userId },
  );

  const deactivateDate = userData?.deactivateDate
    ? format(new Date(userData.deactivateDate ?? ''), 'yyyy-MM-dd')
    : null;
  const currentDate = format(Date.now(), 'yyyy-MM-dd');
  // An exited employee has no payroll after their last working month. Payroll-Compute
  // treats a dismissed user as eligible only while `deactivate_date >= start of the
  // requested month`, so asking for any later month returns zero payrolls and the CTC
  // breakdown renders blank. Anchor on the exit date whenever there is one: for a
  // scheduled (future) exit this is the same month the previous logic picked, and for an
  // employee who has already left it is their final payable month instead of today.
  const basePayrollMonth = deactivateDate ?? currentDate;

  // For mid-month ECSS revisions: use the display month from the backend so PC returns
  // old (before effectiveFrom) or real ECSS (after effectiveFrom) values — never prorated.
  const ecssProfileDisplayMonth =
    (salaryDetailsData as any)?.payrollGetSalaryDetails?.data?.ecssProfileDisplayMonth ?? null;
  const resolvedPayrollMonth = ecssProfileDisplayMonth ?? basePayrollMonth;

  // Clamp after the ECSS override as well — ecssProfileDisplayMonth is derived from today
  // (previous/next month) and has no exited-employee branch, so it too can land past the
  // exit month. Compared at month granularity: 'YYYY-MM' for the override, 'YYYY-MM-DD'
  // for the exit date.
  const payrollMonth =
    deactivateDate && resolvedPayrollMonth.slice(0, 7) > deactivateDate.slice(0, 7)
      ? deactivateDate
      : resolvedPayrollMonth;

  const {
    data: compensationData,
    isLoading: isCompensationDataLoading,
    error: compensationError,
    refetch: refetchCompensationData,
  } = useFetchCompensationDetails({ userId, payrollMonth });

  const isLoading = isUserDataLoading || isSalaryDetailsLoading || isCompensationDataLoading;
  const error = userError || compensationError;

  const refetch = () => {
    refetchUserData();
    refetchSalaryDetails();
    refetchCompensationData();
  };

  return {
    userData,
    compensationData,
    firstName,
    hasPendingRevisionRequest:
      salaryDetailsData?.payrollGetSalaryDetails?.data?.pendingWorkflowRequestExists,
    error,
    isLoading,
    refetch,
  };
};

export default useFetchPayDetailsData;
